// frontend/src/context/AuthContext.js
import React, { createContext, useState, useContext, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getProfile } from '../services/profileService';
import api from '../services/api';
// import { App as AntApp } from 'antd'; // <-- REMOVED

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    // Initialize state from localStorage
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(() => {
        const savedToken = localStorage.getItem('token');
        if (savedToken) {
            try {
                return jwtDecode(savedToken).user;
            } catch (error) {
                return null; // Invalid token
            }
        }
        return null;
    });

    const login = (newToken) => {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        try {
            setUser(jwtDecode(newToken).user);
        } catch (error) {
            setUser(null);
        }
    };

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        // Force reload to clear all state and redirect to login via interceptor
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    }, []);

    const startImpersonation = (newToken) => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
            localStorage.setItem('original_token', currentToken);
        }
        login(newToken);
        window.location.href = '/';
    };

    const exitImpersonation = async () => {
        try {
            await api.post('/auth/impersonate/exit');
        } catch (error) {
            // Best-effort audit log call - still restore the admin's session even if this fails
            console.error('Failed to log impersonation exit:', error);
        }

        const originalToken = localStorage.getItem('original_token');
        localStorage.removeItem('original_token');

        if (originalToken) {
            login(originalToken);
        } else {
            logout();
            return;
        }
        window.location.href = '/';
    };

    let impersonation = null;
    if (token) {
        try {
            impersonation = jwtDecode(token).impersonation || null;
        } catch (error) {
            impersonation = null;
        }
    }

    // --- NEW: Role Synchronization Logic (Cleaned up) ---
    const syncRole = useCallback(async () => {
        if (!token || !user) return false; // Return false if not authenticated

        try {
            // Fetch the user's current data from the database
            const profile = await getProfile();

            // Compare the role currently in the JWT claim/local state with the live database role
            if (profile.role !== user.role) {
                // CRITICAL: Log out the user immediately 
                logout();

                // Return flag indicating a role change occurred
                return true;
            }
            return false;

        } catch (error) {
            // If fetching the profile fails (e.g., 401, user deleted), log out
            if (error.response?.status === 401 || error.response?.status === 404) {
                logout();
                return true;
            }
            return false;
        }
    }, [token, user, logout]);
    // --- END NEW ---

    // --- RBAC Helper ---
    const hasPermission = useCallback((module, permission = 'can_view') => {
        if (!user) return false;
        if (user.role === 'super_admin') return true;

        // Frontend static permission mapping (sync with backend role_permissions)
        const rolePermissions = {
            principal: {
                '*': { can_view: true, can_create: true, can_edit: true, can_delete: true }
            },
            teacher: {
                'students': { can_view: true },
                'attendance': { can_view: true, can_create: true, can_edit: true },
                'marks': { can_view: true, can_create: true, can_edit: true },
                'timetable': { can_view: true },
                'library': { can_view: true },
                'reports': { can_view: true },
                'calendar': { can_view: true }
            },
            librarian: {
                'library': { can_view: true, can_create: true, can_edit: true, can_delete: true },
                'students': { can_view: true },
                'users': { can_view: true }
            }
        };

        const perms = rolePermissions[user.role];
        if (!perms) return false;

        // Check for wildcard
        if (perms['*'] && perms['*'][permission]) return true;

        // Check for specific module
        return perms[module] && perms[module][permission] ? true : false;
    }, [user]);


    return (
        <AuthContext.Provider value={{ token, user, login, logout, isAuthenticated: !!token, syncRole, hasPermission, startImpersonation, exitImpersonation, impersonation }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};